import { type LatLngTuple, type LatLngBoundsExpression, type LatLngBoundsLiteral, type LatLngExpression } from "leaflet";
import { getMedian } from "src/lib/number.helpers";
import { type FlowMapWithFrequencyFeatureCollection } from "src/types/FlowMapWithFrequencyFeatureCollection";

import { defaultMaxLat, defaultMaxLon, defaultMinLat, defaultMinLon } from "../../../../lib/leaflet.helper";

const GEOJSON_FEATURE_CHUNK_SIZE = 4000;

function getFlowmapFeatureLatitudesLongitudes(featureCollection: FlowMapWithFrequencyFeatureCollection): {
  latitudes: number[];
  longitudes: number[];
} {
  const latitudes: number[] = [];
  const longitudes: number[] = [];

  for (const feature of featureCollection.features) {
    if (feature.geometry.type === "LineString") {
      for (const coordinates of feature.geometry.coordinates) {
        longitudes.push(coordinates[0]);
        latitudes.push(coordinates[1]);
      }
    }
  }

  return { latitudes, longitudes };
}

function getFlowmapLatitudesLongitudes(geoData: FlowMapWithFrequencyFeatureCollection): {
  latitudes: number[];
  longitudes: number[];
} {
  return getFlowmapFeatureLatitudesLongitudes(geoData);
}

export function calculateFlowmapBoundingBox(geoData: FlowMapWithFrequencyFeatureCollection): {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
} {
  const { latitudes, longitudes } = getFlowmapLatitudesLongitudes(geoData);

  return {
    minLat: latitudes.length > 0 ? Math.min(...latitudes) : defaultMinLat,
    maxLat: latitudes.length > 0 ? Math.max(...latitudes) : defaultMaxLat,
    minLon: longitudes.length > 0 ? Math.min(...longitudes) : defaultMinLon,
    maxLon: longitudes.length > 0 ? Math.max(...longitudes) : defaultMaxLon,
  };
}

export function calculateFlowmapFeatureSampleCenterAndBounds(geoData: FlowMapWithFrequencyFeatureCollection): {
  center: LatLngTuple;
  bounds: LatLngBoundsLiteral;
} {
  const { minLat, maxLat, minLon, maxLon } = calculateFlowmapBoundingBox(geoData);

  const centerLat = (minLat + maxLat) / 2;
  const distanceLatitude = maxLat - minLat;
  const bufferLatitude = distanceLatitude * 0.1;
  const centerLong = (minLon + maxLon) / 2;
  const distanceLong = maxLon - minLon;
  const bufferLongitude = distanceLong * 0.1;

  const center: LatLngExpression = [centerLat, centerLong];
  const bounds: LatLngBoundsExpression = [
    [minLat - bufferLatitude, minLon - bufferLongitude],
    [maxLat + bufferLatitude, maxLon + bufferLongitude],
  ];
  return { center, bounds };
}

export function calculateFlowmapCenterAndBounds(geoData: FlowMapWithFrequencyFeatureCollection): {
  center: LatLngExpression;
  bounds: LatLngBoundsLiteral;
} {
  const centerLats: number[] = [];
  const centerLong: number[] = [];
  const minLatBounds: number[] = [];
  const minLongBounds: number[] = [];
  const maxLatBounds: number[] = [];
  const maxLongBounds: number[] = [];

  for (let i = 0; i < geoData.features.length; i += GEOJSON_FEATURE_CHUNK_SIZE) {
    const geoDataSample = { type: geoData.type, features: geoData.features.slice(i, i + GEOJSON_FEATURE_CHUNK_SIZE) };
    const { center, bounds } = calculateFlowmapFeatureSampleCenterAndBounds(geoDataSample);
    centerLats.push(center[0]);
    centerLong.push(center[1]);
    minLatBounds.push(bounds[0][0]);
    minLongBounds.push(bounds[0][1]);
    maxLatBounds.push(bounds[1][0]);
    maxLongBounds.push(bounds[1][1]);
  }

  return {
    center: [getMedian(centerLats), getMedian(centerLong)],
    bounds: [
      [Math.min(...minLatBounds), Math.min(...minLongBounds)],
      [Math.max(...maxLatBounds), Math.max(...maxLongBounds)],
    ],
  };
}
